import { useEffect, useMemo } from 'react';

import styled from '@emotion/styled';
import { useNavigate, useParams } from 'react-router-dom';
import { CountryIdentifiers, ROUTES, formatUTCDate } from 'core';
import { clearPreparationCentre, selectPreparationCentre, selectSelectedCountryId } from 'store/country/countrySlice';
import { selectBasketValidDate } from 'store/basket/basketSlice';
import { selectUser } from 'store/user/userSlice';
import { useAppDispatch, useAppSelector } from 'store';
import { useGetCandidateExamDataQuery, useGetDistrictQuery } from 'store/payment/paymentAPI';

import { Divider, LoadingCard, MainContainer, Quote, ReadonlyField } from '@britishcouncil/react-solas-ors3';
import { Button } from '@britishcouncil/react-solas';

import { BangladeshWarningMsg } from './components/BangladeshWarningMsg';
import { ExamCard } from './components/ExamCard';
import { KuwaitWarningMsg } from './components/KuwaitWarningMsg';
import { UaeWarningMsg } from './components/UaeWarningMsg';
import { usePaymentSummaryData } from './usePaymentSummaryData';
import { getTaxInfo } from 'pages/helper/taxInfo';

export const OfflinePaymentSummary = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { referenceNumber } = useParams<{ referenceNumber: string }>();
  const countryId = useAppSelector(selectSelectedCountryId);
  //TODO[GP - future fix]: we should fetch this info from api instead get it from basket store...
  const sessionMilestoneDateEnd = useAppSelector(selectBasketValidDate);
  const pcInfo = useAppSelector(selectPreparationCentre);
  const loggedUser = useAppSelector(selectUser);
  const { data: district, isFetching: districtIsLoading } = useGetDistrictQuery(referenceNumber!);
  const { data: candidateExamData, isFetching: candidateExamDataIsLoading } = useGetCandidateExamDataQuery(
    referenceNumber!,
  );

  const { activeExams, total, preparationCentreName, sessionName, taxRate, currency } = usePaymentSummaryData(
    referenceNumber!,
    candidateExamData?.exams ?? [],
  );

  const isPaid = total === 0;

  const isLoading = useMemo(
    () => candidateExamDataIsLoading || districtIsLoading,
    [candidateExamDataIsLoading, districtIsLoading],
  );

  const endStagePaymentInfo = useMemo(
    () =>
      sessionMilestoneDateEnd.isLastSessionStage
        ? 'After that date, the registration window will be closed.'
        : 'After that date, extra charges will apply.',
    [sessionMilestoneDateEnd.isLastSessionStage],
  );

  const taxInfo = useMemo(() => {
    if (!taxRate) return '';
    return getTaxInfo(taxRate, total);
  }, [taxRate, total]);

  useEffect(() => {
    //TODO[GP - future fix]: strange logic here. pcInfo is cleared on this page, but on online summary not...
    //Additionally, pcInfo will be visible only until user not refresh this page...
    return () => {
      if (pcInfo) {
        dispatch(clearPreparationCentre());
      }
    };
  }, [dispatch, pcInfo]);

  return (
    <MainContainer>
      <h1>Offline payment method selected</h1>

      {isLoading && <LoadingCard>One second, please</LoadingCard>}

      {!isLoading && (
        <>
          {countryId === CountryIdentifiers.BANGLADESH_ID && <BangladeshWarningMsg />}
          {countryId === CountryIdentifiers.UAE_ID && <UaeWarningMsg />}
          {countryId === CountryIdentifiers.KUWAIT_ID && <KuwaitWarningMsg />}

          <Quote title="Registration complete" intent="success">
            Transaction reference number: {referenceNumber}
          </Quote>

          <h2>What's next?</h2>
          <Quote title="Thank you for submitting your exams registration." intent="info">
            {!isPaid && (
              <div ng-if="!vm.isPaid">
                {!pcInfo ? (
                  <p>
                    A place is being held for you at the Exam location <b>{district?.name}</b> which will be confirmed
                    when we receive your full payment by up to and including{' '}
                    <b>{formatUTCDate(sessionMilestoneDateEnd.priceValidTo)} (GMT/BST)</b>. {endStagePaymentInfo}
                  </p>
                ) : (
                  <p>
                    A place is being held for you at the Exam location <b>{district?.name}</b> with the{' '}
                    <b>{pcInfo.preparationCentreName}</b>. Payment must be made to the school by{' '}
                    <b>{formatUTCDate(sessionMilestoneDateEnd.priceValidTo)} (GMT/BST)</b>. {endStagePaymentInfo}
                  </p>
                )}
              </div>
            )}
            <p>Updates regarding your exams will be sent to you by email.</p>
          </Quote>
          <Divider />

          <ReadonlyField label="Registration type">{preparationCentreName ? 'School' : 'Private'}</ReadonlyField>
          <ReadonlyField label="Candidate UID">{candidateExamData?.candidateId}</ReadonlyField>
          <ReadonlyField label="Session">{sessionName}</ReadonlyField>
          {preparationCentreName && <ReadonlyField label="School name">{preparationCentreName}</ReadonlyField>}
          <Divider />

          <h2>You have been registered for the following exams:</h2>
          {activeExams.map((exam, i) => (
            <ExamCard key={i} exam={exam} />
          ))}
          <PriceLabel>
            Total price:{' '}
            <strong>
              {total} {currency}
            </strong>
            <br />
            {taxInfo}
          </PriceLabel>

          <Quote title="A registration confirmation email has been sent to the following addresses:" intent="info">
            {loggedUser?.email}
          </Quote>
          <Button onClick={() => navigate(ROUTES.MY_REGISTRATIONS)}>View registrations summary</Button>
        </>
      )}
    </MainContainer>
  );
};

const PriceLabel = styled.p`
  text-align: right;
  margin-bottom: 0;
  margin: 30px 0;
`;
